import {
  SET_OSCILLOMETRY_VIEW,
  SET_OSCILLOMETRY_REQUEST_ID,
  SET_OSCILLOMETRY_TEST_STARTED,
  SET_OSCILLOMETRY_RESULT,
  SET_OSCILLOMETRY_ERROR,
  SET_OSCILLOMETRY_READBACK,
  SET_OSCILLOMETRY_CANONICAL_SESSION,
  RESET_OSCILLOMETRY_STATE,
  SET_OSCILLOMETRY_VISIT_LABELS,
  SET_OSCILLOMETRY_IQC_PERSIST_STATUS,
} from './actionTypes';
import { OscillometryMeasurement } from '../../Types/Common/MeasurementTypes';

export type OscillometryView =
  | 'BD_ADMIN'
  | 'LABEL_SELECT'
  | 'WAITING'
  | 'TEST_IN_PROGRESS'
  | 'QC_REVIEW'
  | 'COMPLETE'
  | 'ERROR';

export type OscillometryReadbackStatus = 'IDLE' | 'POLLING' | 'MATCHED';

export type IqcPersistStatus = 'IDLE' | 'PERSISTING' | 'PERSISTED' | 'FAILED';

export type OscillometryTestCompleteResult = {
  requestId: string;
  patientId: string;
  testTimestamp: string;
  deviceSerial: string;
  testId: string;
  testType: string;
  timestamp?: string;
};

export type OscillometryReadbackState = {
  status: OscillometryReadbackStatus;
  startedAt: string | null;
  attemptCount: number;
  matchedAverageMeasurementID: string | null;
  matchStrategy: 'requestId' | 'legacyTestId' | null;
};

export type OscillometryCanonicalSession = {
  averageMeasurementID: string;
  measurements: OscillometryMeasurement[];
};

export type OscillometryState = {
  view: OscillometryView;
  currentRequestId: string | null;
  testStartedAt: string | null;
  result: OscillometryTestCompleteResult | null;
  readback: OscillometryReadbackState;
  canonicalSession: OscillometryCanonicalSession | null;
  error: string | null;
  selectedVisitLabels: string[];
  iqcPersistStatus: IqcPersistStatus;
};

const initialState: OscillometryState = {
  view: 'WAITING',
  currentRequestId: null,
  testStartedAt: null,
  result: null,
  readback: {
    status: 'IDLE',
    startedAt: null,
    attemptCount: 0,
    matchedAverageMeasurementID: null,
    matchStrategy: null,
  },
  canonicalSession: null,
  error: null,
  selectedVisitLabels: [],
  iqcPersistStatus: 'IDLE',
};

type OscillometryAction =
  | { type: typeof SET_OSCILLOMETRY_VIEW; payload: OscillometryView }
  | { type: typeof SET_OSCILLOMETRY_REQUEST_ID; payload: string }
  | { type: typeof SET_OSCILLOMETRY_TEST_STARTED; payload: string }
  | { type: typeof SET_OSCILLOMETRY_RESULT; payload: OscillometryTestCompleteResult }
  | { type: typeof SET_OSCILLOMETRY_ERROR; payload: string }
  | { type: typeof SET_OSCILLOMETRY_READBACK; payload: Partial<OscillometryReadbackState> }
  | { type: typeof SET_OSCILLOMETRY_CANONICAL_SESSION; payload: OscillometryCanonicalSession | null }
  | { type: typeof RESET_OSCILLOMETRY_STATE }
  | { type: typeof SET_OSCILLOMETRY_VISIT_LABELS; payload: string[] }
  | { type: typeof SET_OSCILLOMETRY_IQC_PERSIST_STATUS; payload: IqcPersistStatus };

export const oscillometryReducer = (
  state: OscillometryState = initialState,
  action: OscillometryAction
): OscillometryState => {
  switch (action.type) {
    case SET_OSCILLOMETRY_VIEW:
      return {
        ...state,
        view: action.payload,
      };
    case SET_OSCILLOMETRY_REQUEST_ID:
      return {
        ...state,
        currentRequestId: action.payload,
        result: null,
        canonicalSession: null,
        readback: initialState.readback,
        error: null,
      };
    case SET_OSCILLOMETRY_TEST_STARTED:
      return {
        ...state,
        testStartedAt: action.payload,
      };
    case SET_OSCILLOMETRY_RESULT:
      return {
        ...state,
        result: action.payload,
        readback: {
          ...initialState.readback,
          status: 'POLLING',
          startedAt: new Date().toISOString(),
        },
        canonicalSession: null,
        error: null,
      };
    case SET_OSCILLOMETRY_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case SET_OSCILLOMETRY_READBACK:
      return {
        ...state,
        readback: {
          ...state.readback,
          ...action.payload,
        },
      };
    case SET_OSCILLOMETRY_CANONICAL_SESSION:
      return {
        ...state,
        canonicalSession: action.payload,
      };
    case RESET_OSCILLOMETRY_STATE:
      return initialState;
    case SET_OSCILLOMETRY_VISIT_LABELS:
      return {
        ...state,
        selectedVisitLabels: action.payload,
      };
    case SET_OSCILLOMETRY_IQC_PERSIST_STATUS:
      return {
        ...state,
        iqcPersistStatus: action.payload,
      };
    default:
      return state;
  }
};
